import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const destinations = [
  { name: "USA", slug: "usa", tag: "Top Pick", desc: "Most popular destination for international students.", img: "https://images.unsplash.com/photo-1485738422979-f5c462d49f74?auto=format&fit=crop&w=900&q=70", color: "bg-[#c92e34]" },
  { name: "UK", slug: "uk", tag: "Heritage", desc: "Diverse country rich in history and culture.", img: "https://images.unsplash.com/photo-1486299267070-83823f5448dd?auto=format&fit=crop&w=900&q=70", color: "bg-[#282829]" },
  { name: "Canada", slug: "canada", tag: "Budget Friendly", desc: "Academic excellence, affordability and adventure.", img: "https://images.unsplash.com/photo-1517935706615-2717063c2225?auto=format&fit=crop&w=900&q=70", color: "bg-[#c92e34]" },
  { name: "Australia", slug: "australia", tag: "Innovative", desc: "Home to leading universities in Adelaide & Perth.", img: "https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?auto=format&fit=crop&w=900&q=70", color: "bg-[#282829]" },
  { name: "Ireland", slug: "ireland", tag: "Rising", desc: "World-class education with strong industry links.", img: "https://images.unsplash.com/photo-1564959130747-897fb406b9af?auto=format&fit=crop&w=900&q=70", color: "bg-[#c92e34]" },
  { name: "New Zealand", slug: "new-zealand", tag: "Scenic", desc: "Quality education in a safe & welcoming environment.", img: "https://images.unsplash.com/photo-1469521669194-babb45599def?auto=format&fit=crop&w=900&q=70", color: "bg-[#282829]" },
  { name: "Europe", slug: "europe", tag: "Diverse", desc: "Affordable tuition across multiple cultures.", img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=900&q=70", color: "bg-[#c92e34]" },
  { name: "Dubai", slug: "dubai", tag: "Global Hub", desc: "World-class campuses with industry connections.", img: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=900&q=70", color: "bg-[#282829]" },
];

function DestCard({ d, i, span = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 60, rotateX: -20 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: i * 0.07, type: "spring", stiffness: 60, damping: 14 }}
      whileHover={{ rotate: i % 2 ? 1.5 : -1.5, y: -6, scale: 1.01 }}
      style={{ transformPerspective: 1000 }}
      className={`relative group overflow-hidden rounded-3xl border-2 border-[#282829] ${span} brutal-shadow-ink`}
    >
      <Link
        to={`/study-in-${d.slug}`}
        data-testid={`destination-card-${d.name.toLowerCase().replace(" ", "-")}`}
        className="block h-full"
      >
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
          style={{ backgroundImage: `url(${d.img})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#282829]/85 via-[#282829]/30 to-transparent" />
        <div className="relative h-full p-5 flex flex-col justify-between min-h-[260px] md:min-h-[300px]">
          <span className={`self-start ${d.color} text-white text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full border-2 border-[#282829]`}>
            {d.tag}
          </span>
          <div>
            <h3 className="font-display text-white text-4xl md:text-5xl leading-none">{d.name}</h3>
            <p className="text-white/85 text-sm mt-2 max-w-xs">{d.desc}</p>
            <div className="mt-3 inline-flex items-center gap-1 text-white text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity">
              Explore <ArrowUpRight size={16} />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Destinations() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const headingY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="destinations" ref={ref} className="py-10 md:py-20 relative overflow-hidden bg-[#c92e34] text-white border-y-[3px] border-[#282829]" data-testid="destinations-section">
      {/* Dotted overlay pattern */}
      <div
        className="absolute inset-0 opacity-15 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(#fff 1.2px, transparent 1.2px)",
          backgroundSize: "22px 22px",
        }}
      />
      {/* Soft glows */}
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 -right-24 w-96 h-96 rounded-full bg-[#282829]/30 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-10 relative z-10">
        <motion.div style={{ y: headingY }} className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12 md:mb-10 pb-2">
          <div>
            <span className="inline-block bg-white text-[#c92e34] text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border-2 border-[#282829] -rotate-2">Top Destinations</span>
            <h2 className="font-display text-4xl sm:text-5xl md:text-7xl leading-[0.9] mt-4 text-white">
              Pick a <span className="font-handwritten text-[#282829] inline-block -rotate-2">spot</span>.<br />
              Pack a <span className="font-handwritten text-[#282829] inline-block rotate-2">bag</span>.
            </h2>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {destinations.map((d, i) => (
            <DestCard
              key={d.name}
              d={d}
              i={i}
              span={
                i === 0 ? "lg:col-span-2 lg:row-span-2" :
                i === 5 ? "lg:col-span-2" :
                ""
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
}
